import type { ARExportOptions } from '../types';
import type { ExportManager } from '../export-manager.class';
import { openModelViewerARCfn } from './model-viewer-ar.cfn';
import { prepareARScene } from './prepare-ar-scene.cfn';

/**
 * Activate iOS AR Quick Look with USDZ export.
 */
export const activateIOSQuickLookCfn = async (
	self: ExportManager,
	options: ARExportOptions
): Promise<void> => {
	const { arHelper } = self;
	const [tempScene, islandClone] = prepareARScene(self, options);

	try {
		await arHelper.openIosArQuickLook(tempScene);
	} finally {
		// Detach only — do NOT dispose, geometries and materials are shared
		// with the live scene.
		tempScene.remove(islandClone);
	}
};

/**
 * Activate AR. Routes to the best path for the current device:
 *   iOS (Safari, Chrome, Edge, Firefox)      -> AR Quick Look (USDZ built in-page)
 *   Android (Chrome, Samsung Internet) and
 *   any other browser with WebXR AR          -> <model-viewer> WebXR launch sheet (GLB blob)
 *   anything else                            -> error
 *
 * Google Scene Viewer is deliberately not used: it needs a public https URL
 * for the model and cannot read a blob: URL minted in this tab.
 */
export const activateARCfn = async (
	self: ExportManager,
	options: ARExportOptions = {}
): Promise<void> => {
	const { scene, islandHolder } = self.configAccess;
	const { arHelper } = self;

	if (!scene || !islandHolder) {
		throw new Error('Scene or island holder not available. The kitchen may still be loading.');
	}

	if (islandHolder.children.length === 0) {
		throw new Error('Island holder is empty. Kitchen model is not ready yet.');
	}

	// iOS Quick Look is the known-good iOS path; check it first so a false
	// positive on navigator.xr never intercepts iPad Safari.
	if (arHelper.isArQuickLookCandidate()) {
		await activateIOSQuickLookCfn(self, options);
		return;
	}

	if (arHelper.isIOS()) {
		throw new Error('AR is not supported in this browser. On iOS, please use Safari.');
	}

	await openModelViewerARCfn(self, options);
};

/**
 * Check if AR is available via any supported path.
 */
export const isARAvailableCfn = async (self: ExportManager): Promise<boolean> => {
	const { arHelper } = self;
	try {
		if (arHelper.isArQuickLookCandidate()) return true;
		// Chromium browsers on Android provide WebXR AR; the launch sheet
		// explains what is missing on the ones that do not.
		if (arHelper.isAndroid()) return true;
		return await arHelper.supportsPresentation();
	} catch {
		return arHelper.isIOS() || arHelper.isAndroid();
	}
};
